import { Bell, BellOff, BellRing, type LucideIcon } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import {
  disable,
  getPermissionState,
  isIosWithoutPwa,
  requestPermission,
  type PermissionState,
} from "@/notifications/permission";

type StateView = {
  Icon: LucideIcon;
  iconClass: string;
  heading: string;
  description: string;
  actionHint?: string;
};

const ON_ICON_CLASS = "fill-current text-amber-500 origin-top";
const OFF_ICON_CLASS = "fill-current text-gray-400";

const STATE_VIEWS: Record<PermissionState, StateView> = {
  granted: {
    Icon: Bell,
    iconClass: ON_ICON_CLASS,
    heading: "Notifications are on",
    description:
      "You'll get a desktop alert when a new message arrives and this tab isn't focused.",
    actionHint: "Click the bell to turn off.",
  },
  default: {
    Icon: BellRing,
    iconClass: OFF_ICON_CLASS,
    heading: "Get notified about new messages",
    description:
      "Turn on notifications and we'll alert you when a new message arrives and you're working in another tab or window.",
    actionHint: "Click the bell to enable.",
  },
  dismissed: {
    Icon: BellOff,
    iconClass: OFF_ICON_CLASS,
    heading: "Notifications are off",
    description:
      "We won't alert you about new messages while you're away from this tab.",
    actionHint: "Click the bell to turn them back on.",
  },
  denied: {
    Icon: BellOff,
    iconClass: OFF_ICON_CLASS,
    heading: "Notifications are blocked",
    description:
      "Your browser has blocked notifications for this site, so we can't alert you.",
    actionHint:
      "Open the lock icon in the address bar → Site settings → set Notifications to Allow → reload this page.",
  },
  unsupported: {
    Icon: BellOff,
    iconClass: OFF_ICON_CLASS,
    heading: "Notifications aren't available",
    description: "This browser doesn't support desktop notifications.",
  },
};

const IOS_VIEW: StateView = {
  Icon: BellOff,
  iconClass: OFF_ICON_CLASS,
  heading: "Add to Home Screen first",
  description:
    "On iPhone and iPad, desktop notifications only work when the app is installed to the home screen.",
  actionHint:
    "Open the share menu in Safari, choose 'Add to Home Screen', then open the app from the new icon.",
};

const NotificationToggle = () => {
  const [state, setState] = useState<PermissionState>(getPermissionState);
  const [isWorking, setIsWorking] = useState(false);
  const [justEnabled, setJustEnabled] = useState(false);

  useEffect(() => {
    setState(getPermissionState());
  }, []);

  const isIosGuidance = state === "unsupported" && isIosWithoutPwa();
  if (state === "unsupported" && !isIosGuidance) return null;

  const view = isIosGuidance ? IOS_VIEW : STATE_VIEWS[state];
  const { Icon } = view;
  const isOn = state === "granted";
  const isInteractive =
    state === "default" || state === "dismissed" || state === "granted";

  const handleClick = async () => {
    if (!isInteractive || isWorking) return;
    setIsWorking(true);
    try {
      const wasOn = isOn;
      if (wasOn) {
        disable();
      } else {
        await requestPermission();
      }
      const next = getPermissionState();
      setState(next);
      if (!wasOn && next === "granted") setJustEnabled(true);
    } catch (err) {
      console.error("Notification toggle failed", err);
      setState(getPermissionState());
    } finally {
      setIsWorking(false);
    }
  };

  const iconClassName =
    isOn && justEnabled
      ? `${view.iconClass} animate-bell-ring`
      : view.iconClass;

  return (
    <HoverCard openDelay={150} closeDelay={50}>
      <HoverCardTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          onClick={handleClick}
          disabled={!isInteractive || isWorking}
          aria-label={view.heading}
          disableLogging
        >
          <Icon
            className={iconClassName}
            onAnimationEnd={() => setJustEnabled(false)}
          />
        </Button>
      </HoverCardTrigger>
      <HoverCardContent align="end" className="w-72 space-y-1.5">
        <p className="text-sm font-medium">{view.heading}</p>
        <p className="text-xs text-muted-foreground">{view.description}</p>
        {view.actionHint && (
          <p className="text-xs font-medium">{view.actionHint}</p>
        )}
      </HoverCardContent>
    </HoverCard>
  );
};

export default NotificationToggle;
